import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { ButtonBase, Paper, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import ClientLottiePlayer from "../../shared/ClientLottiePlayer";
import PhoneInput from "react-phone-number-input";
import Color from "../../../assets/colors";

export const Main = styled("main")(({ theme }) => ({
  backgroundColor: Color.homepageGrey,
  backgroundImage: `
    linear-gradient(
      180deg,
      rgba(64, 36, 155, 0.10) 1%,
      rgba(64, 36, 155, 0.00) 35%
    ),
    radial-gradient(
      35% 15% at 75% 3%,
      rgba(235, 203, 247, 0.36) 0%,
      rgba(246, 223, 255, 0) 150%
    ),
    radial-gradient(
      25% 20% at 75% 1%,
      rgba(252, 224, 224, 0.33) 0%,
      rgba(228, 192, 59, 0) 150%
    ),
    linear-gradient(
      0deg,
      rgba(64, 36, 155, 0.05) 5%,
      ${Color.homepageGrey} 20%
    )
  `,
  backgroundRepeat: "no-repeat",
  minHeight: "100vh",
  padding: "40px 24px 80px",
  display: "flex",
  justifyContent: "center",
  marginTop: 30,

  [theme.breakpoints.down("md")]: {
    padding: "24px 16px 56px",
  },
}));

export const Inner = styled("div")({
  width: "100%",
  maxWidth: 1240,
});

export const HeroCard = styled(Paper)(({ theme }) => ({
  width: "100%",
  backgroundColor: "transparent",
  padding: "40px",
  display: "flex",
  columnGap: 24,
  alignItems: "center",

  [theme.breakpoints.down("tabletL")]: {
    padding: "24px",
  },

  [theme.breakpoints.down("md")]: {
    flexDirection: "column-reverse",
    marginTop: 40,
    rowGap: 24,
    padding: "20px",
    alignItems: "stretch",
  },

  [theme.breakpoints.down("sm")]: {
    rowGap: 30,
  },
}));

export const HeroCopy = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,

  [theme.breakpoints.down("md")]: {
    textAlign: "left",
  },
}));

export const HeroTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Georgia",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: 56,
  lineHeight: "120%",
  letterSpacing: "1%",
  verticalAlign: "middle",
  color: "#120146",
  marginBottom: 25,

  [theme.breakpoints.down("md")]: {
    fontSize: 36,
    textAlign: "center",
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: 24,
  },
}));

export const HeroDescription = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: 24,
  leadingTrim: "none",
  lineHeight: "150%",
  letterSpacing: "0%",
  textAlign: "justify",
  marginBottom: 40,
  color: "#261754",


  [theme.breakpoints.down("md")]: {
    maxWidth: "unset",
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: 16,
    textAlign: "center",
  },
}));

export const HeroActions = styled("div")(({ theme }) => ({
  display: "flex",
  gap: 16,
  alignItems: "center",

  [theme.breakpoints.down("md")]: {
    justifyContent: "center",
  },
}));

export const HeroCtaButton = styled(ButtonBase)(({ theme }) => ({
  height: 54,
  padding: "12px 24px",
  borderRadius: 8,
  background: Color.buttonGrey,
  color: Color.white,
  fontFamily: "Inter",
  fontWeight: 700,
  fontSize: 20,
  textTransform: "none",
  width: "fit-content",
  justifyContent: "center",
  alignItems: "center",

  [theme.breakpoints.down("md")]: {
    width: "fit-content",
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: 14,
    width: "100%",
    padding: "10px 15px",
  },
}));

export const HeroImageContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  width: 360,

  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

export const HeroImage = styled("img")(({ theme }) => ({
  objectFit: "contain",
  width: 280,
  height: 325,

  [theme.breakpoints.down("sm")]: {
    width: 200,
    height: 200,
  },
}));

export const FeaturedInSection = styled("section")(({ theme }) => ({
  width: "100%",
  marginTop: 30,
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: 40,

  [theme.breakpoints.down("md")]: {
    marginTop: 70,
    gap: 40,
  },
}));

export const FeaturedInTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 700,
  fontSize: 24,
  lineHeight: "32px",
  letterSpacing: "0.2px",
  textAlign: "center",
  color: Color.textHighEmphasis,

  [theme.breakpoints.down("sm")]: {
    fontSize: 16,
  },
}));

export const FeaturedInLogos = styled("div")(({ theme }) => ({
  width: "100%",
  display: "grid",
  gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
  columnGap: 24,
  rowGap: 20,
  alignItems: "center",
  justifyItems: "center",

  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  },
}));

export const FeaturedInLogoItem = styled("div")({
  width: "100%",
  boxSizing: "border-box",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "20px 0",
  minHeight: 28,
  borderRadius: 20,
  border: "1px solid transparent",
  transition: "background-color 180ms ease, border-color 180ms ease",

  // hover effect for logo
  "&:hover": {
    backgroundColor: `${Color.hiredlyPurple}11`,
    borderColor: Color.hiredlyPurple,
  },
});

export const FeaturedInLogoLink = styled("a")({
  display: "flex",
  width: "100%",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
});

export const FeaturedInLogo = styled("img")(({ theme }) => ({
  height: 40,
  width: "auto",
  maxWidth: 140,
  objectFit: "contain",
  opacity: 0.9,

  [theme.breakpoints.down("md")]: {
    maxWidth: 120,
  },

  [theme.breakpoints.down("sm")]: {
    height: 20,
  },
}));

export const FeaturedInLogoText = styled(Typography)({
  fontFamily: "Inter",
  fontSize: 16,
  fontWeight: 800,
  letterSpacing: "0.8px",
  color: Color.black,
  textTransform: "uppercase",
  opacity: 0.85,
});

export const DetailSection = styled("section")(({ theme }) => ({
  width: "100%",
  marginTop: 130,
  padding: "0 40px",

  [theme.breakpoints.down("md")]: {
    marginTop: 80,
  },

  [theme.breakpoints.down("sm")]: {
    padding: "0 10px",
  },
}));

export const DetailGrid = styled("div")(({ theme }) => ({
  width: "100%",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 400px",
  columnGap: 48,
  alignItems: "start",

  [theme.breakpoints.down("tabletL")]: {
    gridTemplateColumns: "minmax(0, 1fr) 360px",
    columnGap: 32,
  },

  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    rowGap: 40,
  },
}));

export const DetailLeft = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 10,
  maxWidth: 720,
});

export const DetailTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Georgia",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: "36px",
  lineHeight: "120%",
  letterSpacing: "1%",
  color: "#120146",

  [theme.breakpoints.down("md")]: {
    fontSize: 28,
    lineHeight: "33.6px",
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: 24,
    textAlign: "center",
  },
}));

export const DetailSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: "20px",
  leadingTrim: "NONE",
  lineHeight: "150%",
  letterSpacing: "0.15%",
  verticalAlign: "middle",
  color: "#00000099",

  [theme.breakpoints.down("sm")]: {
    fontSize: 16,
    textAlign: "center",
  },
}));

export const RevealList = styled("div")({
  display: "flex",
  flexDirection: "column",
  marginTop: 25,
  gap: 24,
});

export const RevealItem = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: "24px 0 15px",

  [theme.breakpoints.down("md")]: {
    padding: "20px 0",
  },
}));

export const RevealDivider = styled("div")({
  height: 1,
  width: "100%",
  backgroundColor: "rgba(81, 42, 204, 0.18)",
});

export const RevealValue = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 700,
  fontStyle: "Bold",
  fontSize: 40,
  leadingTrim: "NONE",
  lineHeight: "120%",
  letterSpacing: 0.15,
  verticalAlign: "middle",
  color: "#40249B",

  [theme.breakpoints.down("md")]: {
    fontSize: 32,
    lineHeight: "38.4px",
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: 24,
  },
}));

export const RevealDescription = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: 20,
  leadingTrim: "NONE",
  lineHeight: "150%",
  letterSpacing: 0.15,
  verticalAlign: "middle",
  color: "#000000DE",

  [theme.breakpoints.down("sm")]: {
    fontSize: 16,
  },
}));

export const DetailRight = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  position: "sticky",
  top: 150,
  alignSelf: "start",

  [theme.breakpoints.down("md")]: {
    marginTop: 80,
    position: "static",
    top: "auto",
  },
}));

export const LeadFormCard = styled(Paper)({
  width: "100%",
  borderRadius: 16,
  overflow: "hidden",
  border: `2px solid ${Color.hiredlyPurple}`,
  boxShadow: "0 10px 24px rgba(38, 23, 84, 0.18)",
  backgroundColor: Color.white,
});

export const LeadFormHeader = styled("div")(({ theme }) => ({
  backgroundColor: Color.darkPurple,
  padding: "14px 18px",

  [theme.breakpoints.down("sm")]: {
    padding: "14px 12px",
  },
}));

export const LeadFormHeaderTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 700,
  fontStyle: "Bold",
  fontSize: 24,
  lineHeight: "150%",
  letterSpacing: "0%",
  color: "#FFFFFFDE",
  textAlign: "center",

  [theme.breakpoints.down("sm")]: {
    fontSize: 20,
  },
}));

export const LeadFormBody = styled("div")({
  padding: "18px 18px 20px",
  backgroundColor: "#EFEFFD52",
});

export const LeadFormDescription = styled(Typography)({
  fontFamily: "Inter",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: 16,
  lineHeight: "150%",
  letterSpacing: "0.15px",
  color: "#00000099",
  marginTop: 10,
});

export const LeadFormSuccess = styled("div")(({ theme }) => ({
  padding: "10px 0 6px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: 10,

  [theme.breakpoints.down("sm")]: {
    padding: "8px 0 4px",
  },
}));

export const LeadFormSuccessIcon = styled(ClientLottiePlayer)(({ theme }) => ({
  width: 200,
  height: 200,
  display: "block",

  [theme.breakpoints.down("md")]: {
    width: 120,
    height: 120,
  },
}));

export const LeadFormSuccessTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 600,
  fontStyle: "Semi Bold",
  fontSize: 24,
  lineHeight: "150%",
  letterSpacing: "0%",
  textAlign: "center",
  color: "#000000DE",
  marginTop: 2,

  [theme.breakpoints.down("sm")]: {
    fontSize: 16,
  },
}));

export const LeadFormSuccessText = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: 20,
  lineHeight: "150%",
  letterSpacing: "0.15px",
  textAlign: "center",
  verticalAlign: "middle",
  color: "#000000DE",
  marginTop: 10,

  [theme.breakpoints.down("md")]: {
    marginTop: 0,
    width: "90%",
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: 16,
  },
}));

export const LeadFormResetButton = styled(ButtonBase)(({ theme }) => ({
  marginTop: 50,
  height: 50,
  width: "100%",
  borderRadius: 8,
  padding: "0 16px",
  backgroundColor: Color.buttonGrey,
  color: "#FFFFFFDE",
  fontFamily: "Inter",
  fontSize: 20,
  fontWeight: 700,
  textTransform: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",

  "&:hover": {
    backgroundColor: Color.black,
  },

  [theme.breakpoints.down("md")]: {
    marginTop: 20,
    width: "fit-content",
    padding: "0 24px",
    height: 48,
    fontSize: 16,
  },

  [theme.breakpoints.down("sm")]: {
    width: "100%",
    marginTop: 80,
  },
}));

export const LeadFormFields = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  marginTop: 25,

  [theme.breakpoints.between("sm", "md")]: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    columnGap: 16,
    rowGap: 16,
  },
}));

export const LeadFormField = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 6,
});

export const LeadFormFieldLabel = styled("label")({
  fontFamily: "Inter",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: 14,
  leadingTrim: "NONE",
  lineHeight: "150%",
  letterSpacing: "0.15px",
  color: "#000000DE",
});

export const LeadFormErrorRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 6,
  color: "#FF4D4F",
});

export const LeadFormErrorIcon = styled(ErrorOutlineIcon)({
  fontSize: 14,
});

export const LeadFormErrorText = styled(Typography)({
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: 12,
  lineHeight: "16px",
  color: "#FF4D4F",
});

export const LeadFormTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    backgroundColor: Color.white,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.12)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.24)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: Color.hiredlyPurple,
  },
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: "#FF4D4F",
  },
  "& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#FF4D4F",
  },
  "& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline":
    {
      borderColor: "#FF4D4F",
    },
  "& .MuiInputBase-input": {
    fontFamily: "Inter",
    fontSize: 14,
  },
});

export const LeadFormPhoneInput = styled(PhoneInput)({
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: 40,
  borderRadius: 8,
  border: "1px solid rgba(0, 0, 0, 0.12)",
  backgroundColor: Color.white,
  overflow: "hidden",

  "&.PhoneInput--error": {
    borderColor: "#FF4D4F",
  },

  "&.PhoneInput--focus": {
    borderColor: Color.hiredlyPurple,
  },

  "&.PhoneInput--error.PhoneInput--focus": {
    borderColor: "#FF4D4F",
  },

  "& .PhoneInputCountry": {
    position: "relative",
    alignSelf: "stretch",
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "0 10px",
    backgroundColor: "#EDEBFF",
    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
  },

  "& .PhoneInputCountrySelect": {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
    zIndex: 1,
    border: 0,
    opacity: 0,
    cursor: "pointer",
  },

  "& .PhoneInputCountryIcon": {
    width: 20,
    height: 14,
    display: "flex",
    alignItems: "center",
  },

  "& .PhoneInputCountryIconImg": {
    display: "block",
    width: "100%",
    height: "100%",
  },

  "& .PhoneInputCountrySelectArrow": {
    display: "block",
    width: "0.3em",
    height: "0.3em",
    marginLeft: "0.35em",
    borderStyle: "solid",
    borderColor: "inherit",
    borderTopWidth: 0,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderRightWidth: 1,
    transform: "rotate(45deg)",
    opacity: 0.45,
  },

  "& .PhoneInputInput": {
    flex: 1,
    minWidth: 0,
    height: "100%",
    border: 0,
    outline: 0,
    backgroundColor: Color.white,
    fontFamily: "Inter",
    fontSize: 14,
    padding: "0 12px",
  },
});

export const LeadFormSubmitButton = styled(ButtonBase)(({ theme }) => ({
  marginTop: 18,
  width: "100%",
  height: 48,
  borderRadius: 8,
  backgroundColor: Color.buttonGrey,
  color: "#FFFFFFDE",
  fontFamily: "Inter",
  fontSize: 16,
  fontWeight: 800,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,

  "&.Mui-disabled": {
    opacity: 0.7,
    cursor: "not-allowed",
  },

  [theme.breakpoints.between("sm", "md")]: {
    width: "fit-content",
    padding: "0 24px",
    marginLeft: "auto",
    marginRight: "auto",
  },

  [theme.breakpoints.down("md")]: {
    marginTop: 30,
    marginBottom: 10,
  },
}));

export const QuoteBlock = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  marginTop: 30,
  textAlign: "center",
  alignSelf: "stretch",
  gap: 6,
});

export const QuoteText = styled(Typography)({
  width: "100%",
  fontFamily: "Inter",
  fontWeight: 600,
  fontStyle: "italic",
  fontSize: 16,
  lineHeight: "150%",
  letterSpacing: "0.15px",
  verticalAlign: "middle",
  color: "#00000099",
});

export const QuoteAuthor = styled(Typography)({
  display: "inline-block",
  maxWidth: "100%",
  fontFamily: "Inter",
  fontWeight: 400,
  fontStyle: "italic",
  fontSize: 16,
  lineHeight: "150%",
  letterSpacing: "0.15px",
  textAlign: "left",
  verticalAlign: "middle",
  color: "#00000099",
});

export const ReportHelpsSection = styled("section")(({ theme }) => ({
  width: "100%",
  marginTop: 96,

  [theme.breakpoints.down("md")]: {
    marginTop: 72,
  },
}));

export const ReportHelpsInner = styled("div")({
  maxWidth: 720,
});

export const ReportHelpsTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Georgia",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: 36,
  lineHeight: "120%",
  letterSpacing: "1%",
  color: "#120146",

  [theme.breakpoints.down("md")]: {
    fontSize: 30,
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: 24,
    textAlign: "center",
  },
}));

export const ReportHelpsSubtitle = styled(Typography)(({ theme }) => ({
  marginTop: 10,
  fontFamily: "Inter",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: 20,
  lineHeight: "150%",
  letterSpacing: "0.15%",
  verticalAlign: "middle",
  color: "#00000099",

  [theme.breakpoints.down("sm")]: {
    fontSize: 16,
    textAlign: "center",
  },
}));

export const ReportHelpsList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 28,
  marginTop: 40,

  [theme.breakpoints.down("md")]: {
    gap: 50,
    marginTop: 28,
  },

  [theme.breakpoints.down("sm")]: {
    gap: 20,
  },
}));

export const ReportHelpsItem = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "120px minmax(0, 1fr)",
  columnGap: 24,
  alignItems: "center",

  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "88px minmax(0, 1fr)",
    columnGap: 18,
    alignItems: "start",
  },

  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
    columnGap: 0,
    rowGap: 10,
  },
}));

export const ReportHelpsImage = styled("img")(({ theme }) => ({
  width: 120,
  height: 120,
  objectFit: "contain",

  [theme.breakpoints.down("md")]: {
    width: 88,
    height: 88,
  },

  [theme.breakpoints.down("sm")]: {
    width: 154,
    height: 154,
    justifySelf: "center",
  },
}));

export const ReportHelpsItemTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 600,
  fontStyle: "Semi Bold",
  fontSize: 24,
  lineHeight: "150%",
  letterSpacing: "0%",
  verticalAlign: "middle",
  color: "#000000DE",

  [theme.breakpoints.down("sm")]: {
    fontSize: 20,
    textAlign: "center",
  },
}));

export const ReportHelpsItemDescription = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: 20,
  lineHeight: "150%",
  letterSpacing: "0.15%",
  verticalAlign: "middle",
  color: "#000000DE",

  [theme.breakpoints.down("sm")]: {
    fontSize: 16,
    textAlign: "center",
  },
}));

export const ReportMattersSection = styled("section")(({ theme }) => ({
  width: "100%",
  marginTop: 100,

  [theme.breakpoints.down("md")]: {
    marginTop: 80,
  },
}));

export const ReportMattersTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Georgia",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: 36,
  leadingTrim: "none",
  lineHeight: "120%",
  letterSpacing: "1%",
  color: "#120146",

  [theme.breakpoints.down("md")]: {
    fontSize: 30,
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: 24,
    textAlign: "center",
  },
}));

export const ReportMattersList = styled("ul")(({ theme }) => ({
  listStyle: "none",
  margin: "28px 0 0",
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: 20,

  [theme.breakpoints.down("md")]: {
    gap: 14,
    marginTop: 22,
  },
}));

export const ReportMattersItem = styled("li")({
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
});

export const ReportMattersIcon = styled(CheckCircleOutlineIcon)({
  marginTop: 2,
  fontSize: 18,
  color: "#261754",
  flexShrink: 0,
  marginTop: 6,
});

export const ReportMattersText = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontStyle: "Semi Bold",
  fontSize: 20,
  lineHeight: "150%",
  letterSpacing: "0.15%",
  textAlign: "justify",
  verticalAlign: "middle",
  color: "#261754",

  "& strong": {
    fontWeight: 700,
    color: "#261754",
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: 16,
  },
}));

export const Card = styled(Paper)({
  borderRadius: 16,
  border: "1px solid rgba(64, 36, 155, 0.18)",
});

export const CardInner = styled("div")(({ theme }) => ({
  padding: 24,

  [theme.breakpoints.down("md")]: {
    padding: 16,
  },
}));

export const NotFoundTitle = styled(Typography)({
  fontFamily: "Inter",
  fontSize: 16,
  fontWeight: 700,
  color: "#261754",
  marginBottom: 8,
});

export const NotFoundDescription = styled(Typography)({
  fontFamily: "Inter",
  fontSize: 14,
  color: "rgba(0, 0, 0, 0.6)",
});
